import { BasicJob, Job } from 'src/types';

/** @deprecated Use `Job` directly instead. */
export type QueryJob = Job;

import { UseQueryOptions, useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '..';
import { JOB_QUERY_KEY, JOB_UNASSIGN_KEY, ENTITY_KEY, EVENTS_QUERY_KEY, LIST_KEY, PUT_KEY } from '../queryKeys';
import { queryYemboApiCall, queryYemboApiCallWithReturnValue, YemboApiError } from '../queryYemboApiCalls';
import { UnassignMutationError } from './errors';

const url = 'job';

type UseJobOptions = Partial<UseQueryOptions<Job, unknown>> & {
  shouldSkipErrorAlert?: boolean;
};

/** `GET /job/:key` answers the job itself, with its assignee's `basicEmployee`. */
const getFullJob = async (jobKey: string, shouldSkipErrorAlert?: boolean): Promise<Job> => {
  const job = await queryYemboApiCallWithReturnValue<Job>({
    url: `${url}/${jobKey}`,
    shouldNotifyAndThrowError: !shouldSkipErrorAlert,
  });

  if (!job) throw new Error('Job not found');

  return job;
};

/** One page of `GET /job`, the job listing's search (routes/Job.ts). */
export type JobSearchPage = {
  jobs: BasicJob[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
};

/**
 * Search the jobs the caller may read. `params` is the query string built by
 * the listing's `buildJobSearchParams`; the API answers an unknown sort,
 * status or direction with a 400 naming it, which is surfaced like any error.
 */
export const searchJobs = async (params: URLSearchParams, signal?: AbortSignal): Promise<JobSearchPage> => {
  const page = await queryYemboApiCallWithReturnValue<JobSearchPage>({
    url,
    urlParams: params,
    shouldNotifyAndThrowError: true,
    errorOnNotFound: true,
    signal,
  });

  if (!page) throw new Error('Job search failed');

  return page;
};

export const useJobQuery = (jobKey: string | undefined, options?: UseJobOptions) =>
  useQuery({
    queryKey: [JOB_QUERY_KEY, ENTITY_KEY, jobKey],
    queryFn: () => getFullJob(jobKey ?? '', options?.shouldSkipErrorAlert),
    ...options,
    enabled: !!jobKey && options?.enabled !== false,
  });

/** Invalidate a specific job query */
export const invalidateJob = (jobKey: string) =>
  queryClient.invalidateQueries({
    queryKey: [JOB_QUERY_KEY, ENTITY_KEY, jobKey],
  });

/** The job fields an employee edits in place; `key` names the job and is not sent in the body. */
export type PutJobParams = { key: string } & Partial<Pick<Job, 'claimNumber' | 'status' | 'employeeKey' | 'lossDate'>>;

const putJob = async ({ key, ...fields }: PutJobParams) =>
  queryYemboApiCallWithReturnValue<Job>({
    // PUT /job/:key — the API has no keyless PUT /job.
    url: `${url}/${key}`,
    method: 'PUT',
    body: JSON.stringify(fields),
    shouldNotifyAndThrowError: true,
  });

/**
 * Mutation to edit a job's own fields. The cached job is patched optimistically
 * so the details panel shows the new value at once, then refetched to settle.
 */
export const usePutJob = () => {
  const mutationKey = [JOB_QUERY_KEY, PUT_KEY];

  return useMutation({
    mutationKey,
    mutationFn: putJob,
    onMutate: async ({ key, ...fields }) => {
      const queryKey = [JOB_QUERY_KEY, ENTITY_KEY, key];
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<Job>(queryKey);
      queryClient.setQueryData<Job>(queryKey, (current) => current && { ...current, ...fields });
      return { previous };
    },
    onError: (_error, { key }, context) => {
      if (context?.previous) queryClient.setQueryData([JOB_QUERY_KEY, ENTITY_KEY, key], context.previous);
    },
    onSettled: (_data, _error, { key }) => invalidateJob(key),
  });
};

type PutUnassignJobParams = {
  jobKey: string;
  confirmCancelAppointments?: boolean;
};
const putUnassignJob = async ({ jobKey, confirmCancelAppointments }: PutUnassignJobParams) => {
  const body = {
    key: jobKey,
    employeeKey: null,
    confirmCancelAppointments: !!confirmCancelAppointments,
  };

  const response = await queryYemboApiCall({
    // PUT /job/:key — the API has no keyless PUT /job.
    url: `${url}/${jobKey}`,
    method: 'PUT',
    body: JSON.stringify(body),
  });

  if (response.ok) return response;

  let errorJson: unknown;
  try {
    errorJson = await response.json();
  } catch {
    errorJson = undefined;
  }

  if (response.status === 409 && UnassignMutationError.isPendingAppointmentsCancellationErrorBody(errorJson)) {
    throw new UnassignMutationError(
      'Pending appointment cancellation',
      errorJson.reason,
      errorJson.affectedAppointments
    );
  }

  throw new YemboApiError(response, errorJson);
};

/**
 * Mutation to unassign an employee from a job.
 *
 * On a 409 PENDING_APPOINTMENT_CANCELLATION the mutation throws an `UnassignMutationError`
 * so the caller can show a confirmation modal and re-trigger with `confirmCancelAppointments: true`.
 */
export const useUnassignJob = () => {
  const mutationKey = [JOB_QUERY_KEY, PUT_KEY, JOB_UNASSIGN_KEY];

  return useMutation({
    mutationKey,
    mutationFn: putUnassignJob,
    onSuccess: (_, { jobKey, confirmCancelAppointments }) => {
      queryClient.invalidateQueries({
        queryKey: [JOB_QUERY_KEY, ENTITY_KEY, jobKey],
      });

      if (confirmCancelAppointments) {
        queryClient.invalidateQueries({
          queryKey: [EVENTS_QUERY_KEY, LIST_KEY],
        });
      }
    },
  });
};
